import React from "react";
import {Avatar, Box} from "@material-ui/core";
import {blue, grey} from "@material-ui/core/colors";
import {useTranslate} from "@tolgee/react";
import {getInitials} from "tools/common/getInitials";

type ShowOrganiserOption = {
  value: string;
  label?: string;
  company?: string;
};

type ReviewerAttributionProps = {
  // An organiser-type reviewer is identified by `reviewerOrganiserId`; an
  // email-type reviewer by `reviewerEmail`. The email branch wins so a stray
  // non-null `reviewerOrganiserId` can't route an email reviewer into the
  // organiser branch (blank name).
  reviewerOrganiserId?: string | null;
  reviewerEmail?: string | null;
  // The localized "Organiser" role label shown under an organiser reviewer.
  localizedOrganiser: string | null;
  showOrganiserList?: ShowOrganiserOption[];
  // Preformatted decision timestamp (approval / rejection), e.g.
  // "24 Jul 2026, 12:11" — rendered top-right. Omitted when the reviewer
  // hasn't decided yet.
  timestamp?: string | null;
};

/**
 * Two-line reviewer attribution header (avatar + name/role) shared by the
 * single- and multiple-reviewer organiser response blocks. An organiser-type
 * reviewer shows their name/company over the localized "Organiser" role; an
 * email-type reviewer shows "User" over "By {email}".
 */
const ReviewerAttribution: React.FC<ReviewerAttributionProps> = ({
  reviewerOrganiserId,
  reviewerEmail,
  localizedOrganiser,
  showOrganiserList,
  timestamp,
}) => {
  const {t} = useTranslate();
  const isEmailReviewer = !!reviewerEmail;
  const org = isEmailReviewer
    ? undefined
    : showOrganiserList?.find((o) => o.value === reviewerOrganiserId);

  const fallbackName = t("common.tasks.multi_reviewer.user");
  const label = org?.label ?? fallbackName;
  const primaryLine = isEmailReviewer
    ? fallbackName
    : `${org?.label ?? ""}${org?.company ? ` ${org.company}` : ""}`.trim() ||
      fallbackName;
  const secondaryLine = isEmailReviewer
    ? t("common.tasks.reviewer.by_value_caps", {value: reviewerEmail})
    : localizedOrganiser;

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      mb={0.5}
    >
      <Box display="flex" alignItems="center">
        <Avatar
          variant="circle"
          style={{
            background: blue.A100,
            color: blue[900],
            width: 32,
            height: 32,
            fontSize: 14,
          }}
        >
          {getInitials(label)}
        </Avatar>
        <Box ml={1} style={{display: "flex", flexDirection: "column"}}>
          <Box style={{fontWeight: 500}}>{primaryLine}</Box>
          <Box
            style={{
              fontSize: "12px",
              fontWeight: 400,
              color: grey[500],
            }}
          >
            {secondaryLine}
          </Box>
        </Box>
      </Box>
      {!!timestamp && (
        <Box
          style={{
            marginLeft: 8,
            fontSize: "12px",
            fontWeight: 400,
            color: grey[500],
            whiteSpace: "nowrap",
            textTransform: "uppercase",
          }}
        >
          {timestamp}
        </Box>
      )}
    </Box>
  );
};

export default ReviewerAttribution;
